import { Link, useNavigate } from "react-router-dom";
import { CalendarClock, ClipboardList, RotateCcw, Truck } from "lucide-react";
import { useI18n } from "../../../i18n";
import { useCart } from "../../../context/CartContext";
import { fmtDate, fmtMoney, fmtTime, fmtWeekday } from "../../../lib/format";
import { reorderInto } from "../reorder";
import { StatusBadge } from "../../../components/common/StatusBadge";
import { SkeletonBlock } from "../../../components/common/EmptyState";

const base = "rounded-2xl p-4 shadow-[0_1px_3px_rgba(11,37,69,0.06)] sm:p-5 animate-in fade-in slide-in-from-bottom-2 duration-500";
const card = `${base} bg-white`;

export const DeliveryCard = ({ delivery }) => {
  const { t, lang } = useI18n();
  if (delivery.isLoading) return <SkeletonBlock className="h-32" />;
  const next = delivery.data?.slots?.[0];
  return (
    <div className={`${base} bg-navy text-white`} data-testid="next-delivery-card">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wider text-white/60">{t("home.nextDelivery")}</span>
        <Truck className="h-5 w-5 text-brand" />
      </div>
      {next ? (
        <>
          <p className="mt-2 text-2xl font-bold capitalize" data-testid="next-delivery-date">{fmtWeekday(next.date, lang)} {fmtDate(next.date)}</p>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-brand" data-testid="next-delivery-cutoff"><CalendarClock className="h-4 w-4" /> {t("home.orderBy", { day: fmtWeekday(next.order_by, lang), time: fmtTime(next.order_by) })}</p>
          <p className="mt-1 text-xs text-white/60">{t("home.area", { area: delivery.data.area[`name_${lang}`] })}</p>
        </>
      ) : <p className="mt-2 text-sm text-white/70">{t("home.noDelivery")}</p>}
    </div>
  );
};

export const LastOrderCard = ({ orders, shop }) => {
  const { t } = useI18n();
  const cart = useCart();
  const nav = useNavigate();
  if (orders.isLoading) return <SkeletonBlock className="h-32" />;
  const last = (orders.data || []).find((o) => o.status !== "cancelled");
  return (
    <div className={card} data-testid="last-order-card">
      <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{t("home.lastOrder")}</span>
      {last ? (
        <>
          <Link to={`/app/orders/${last.local_id}`} className="mt-2 flex items-center justify-between gap-2" data-testid="last-order-link">
            <div>
              <p className="text-base font-semibold text-navy">{last.softone_doc_no || t(last.status === "pending_approval" ? "orders.pendingNoDoc" : `status.${last.status}`)}</p>
              <p className="text-xs text-slate-500">{fmtDate(last.order_date)} · {t("checkout.itemsCount", { n: last.lines.length })} · {fmtMoney(last.gross_total)}</p>
            </div>
            <StatusBadge status={last.status} customer />
          </Link>
          <button type="button" onClick={() => reorderInto(cart, last.lines, shop, t) && nav("/app/cart")} data-testid="last-order-reorder-button"
            className="mt-3 flex h-11 w-full items-center justify-center gap-2 rounded-full bg-brand/10 text-sm font-semibold text-brand hover:bg-brand/15"><RotateCcw className="h-4 w-4" /> {t("home.reorder")}</button>
        </>
      ) : <p className="mt-2 flex items-center gap-2 text-sm text-slate-500"><ClipboardList className="h-4 w-4" /> {t("home.noOrders")}</p>}
    </div>
  );
};
